import { AccessBadgeList } from './AccessBadgeList';
import type { ExtranetInvitationOutput } from '../../types/extranet-api.types';
import { TableErrorRow } from '../common/TableErrorRow';
import { IconInbox } from '../incidents/icons';

interface InvitationsTableProps {
  items: ExtranetInvitationOutput[];
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  error: unknown;
  onResend: (invitation: ExtranetInvitationOutput) => void;
  resendingId: string | null;
}

const SKELETON_ROWS = 4;
const COL_COUNT = 4; // Nom · Email · Accès · Actions

export const InvitationsTable = ({
  items,
  isLoading,
  isFetching,
  isError,
  error,
  onResend,
  resendingId,
}: InvitationsTableProps) => (
  <div className="bg-white border border-zinc-200 rounded-md overflow-hidden">
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead>
          <tr className="border-b border-zinc-200 bg-zinc-50/40">
            <Th>Nom</Th>
            <Th>Email</Th>
            <Th>Accès</Th>
            <Th align="right">Actions</Th>
          </tr>
        </thead>
        <tbody className={isFetching && !isLoading ? 'opacity-60 transition-opacity' : ''}>
          {isLoading ? (
            Array.from({ length: SKELETON_ROWS }).map((_, i) => <SkeletonRow key={i} />)
          ) : isError ? (
            <TableErrorRow colSpan={COL_COUNT} error={error} fallbackTitle="Impossible de charger les invitations" />
          ) : items.length === 0 ? (
            <EmptyRow />
          ) : (
            items.map(invitation => (
              <tr
                key={invitation.invitationId}
                className="border-b border-zinc-100 last:border-b-0 hover:bg-zinc-50/60 transition-colors duration-100"
              >
                <td className="px-4 py-3 text-zinc-900 whitespace-nowrap font-medium">
                  {invitation.firstName} {invitation.lastName}
                </td>
                <td className="px-4 py-3 font-mono text-xs tabular-nums text-zinc-700 whitespace-nowrap">
                  {invitation.email}
                </td>
                <td className="px-4 py-3">
                  <AccessBadgeList access={invitation.access} />
                </td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <button
                    type="button"
                    onClick={() => onResend(invitation)}
                    disabled={resendingId === invitation.invitationId}
                    className="text-xs text-zinc-700 hover:text-zinc-900 hover:underline underline-offset-2 disabled:opacity-50 disabled:no-underline"
                  >
                    {resendingId === invitation.invitationId ? 'Envoi…' : "Renvoyer l'invitation"}
                  </button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  </div>
);

const Th = ({ children, align = 'left' }: { children: React.ReactNode; align?: 'left' | 'right' }) => (
  <th
    className={`px-4 py-2.5 text-xs font-medium uppercase tracking-wide text-zinc-500 ${align === 'right' ? 'text-right' : 'text-left'}`}
  >
    {children}
  </th>
);

const SkeletonRow = () => (
  <tr className="border-b border-zinc-100 last:border-b-0">
    {Array.from({ length: COL_COUNT }).map((_, i) => (
      <td key={i} className="px-4 py-3">
        <div className="h-3 rounded-sm skeleton-pulse" style={{ width: `${[60, 75, 70, 40][i] ?? 50}%` }} />
      </td>
    ))}
  </tr>
);

const EmptyRow = () => (
  <tr>
    <td colSpan={COL_COUNT} className="px-4 py-16">
      <div className="flex flex-col items-center justify-center gap-3 text-center">
        <IconInbox width={28} height={28} className="text-zinc-300" />
        <div>
          <p className="text-sm text-zinc-900">Aucune invitation en attente.</p>
          <p className="text-xs text-zinc-500 mt-1">Les invitations acceptées disparaissent de cette liste.</p>
        </div>
      </div>
    </td>
  </tr>
);
